import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useLocation, useSearchParams } from "react-router-dom";
import { ChevronLeft } from "@gravity-ui/icons";
import { getArchiveSegmentUrl, getVideoSettings, listArchiveRange } from "../../services/liveArchive";
import { getCamera } from "../../services/cameras";
import { describeSingleTimezone } from "../../utils/cameraTimezone";
import { DateInput, TimeInput } from "../../components/DateTimeInput";
import "./ArchivePage.css";

const pad = (value) => String(value).padStart(2, "0");

const todayDateValue = () => {
  const date = new Date();
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

const currentTimeValue = () => {
  const date = new Date();
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const formatSegmentTime = (value) => {
  if (!value) return "";
  const timePart = value.includes("T") ? value.split("T")[1] : value;
  return timePart.slice(0, 8);
};

const formatSize = (bytes) => {
  if (!bytes) return "";
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)}Kb`;
  return `${(bytes / 1024 / 1024).toFixed(1)}Mb`;
};

// ТЗ №43: «нет доступа» и «записей нет» — разные состояния; сырой текст
// ответа backend пользователю не показывается.
const getErrorMessage = (error) => {
  const status = error?.response?.status;
  const detail = error?.response?.data?.detail || error?.response?.data?.message;

  if (status === 401) return "Требуется авторизация";
  if (status === 403) return "Нет доступа к записям этой камеры";
  if (status === 404) return "Камера недоступна";
  if (status === 400 && String(detail || "").includes("timezone")) return "Некорректный часовой пояс";
  if (status === 502) return "Видеосервис временно недоступен";
  return "Не удалось загрузить архив";
};

/**
 * ТЗ №43: значение интервала из адреса берётся, только если это настоящая
 * дата календаря (YYYY-MM-DD) или время HH:MM (часы < 24, минуты < 60);
 * иначе параметр игнорируется.
 */
export const readArchiveUrlParam = (kind, value) => {
  const raw = typeof value === "string" ? value : "";
  if (kind === "date") {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
    if (!match) return "";
    const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
    const date = new Date(Date.UTC(year, month - 1, day));
    const real = date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
    return real ? raw : "";
  }
  const match = /^(\d{2}):(\d{2})$/.exec(raw);
  if (!match) return "";
  return Number(match[1]) < 24 && Number(match[2]) < 60 ? raw : "";
};

/**
 * Бэкенд архива разбирает время строго как HH:MM:SS, а поля выбирают только
 * часы и минуты — секунды дописываем здесь. Для конца периода подставляем "59",
 * иначе последняя выбранная минута не попала бы в диапазон целиком.
 */
export const normalizeTimeParam = (value, fallback, seconds = "00") => {
  const match = /^(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(value || "");
  if (!match) return fallback;
  return `${match[1]}:${match[2]}:${match[3] || seconds}`;
};

const normalizeSegments = (data) =>
  (data?.items || []).map((item) => ({
    segment_ref: item.segment_ref,
    started_at_local: item.started_at_local || item.started_at,
    ended_at_local: item.ended_at_local || item.ended_at,
    size_bytes: item.size_bytes,
  })).filter((item) => item.segment_ref);

const ArchivePage = () => {
  const { cameraId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  // ТЗ №43: выбранный интервал живёт в адресе — Back и reload его сохраняют.
  const urlDate = readArchiveUrlParam("date", searchParams.get("date"));
  const urlFrom = readArchiveUrlParam("time", searchParams.get("from"));
  const urlTo = readArchiveUrlParam("time", searchParams.get("to"));
  const fallback = location.pathname.startsWith("/equipment") ? "/equipment" : "/cameras";

  const [cameraName, setCameraName] = useState(`Камера ${cameraId}`);
  // ТЗ №2 §10.1: единственный часовой пояс — Camera.display_timezone; отдельного
  // editable timezone архива нет, здесь только read-only индикация.
  const [cameraTimezone, setCameraTimezone] = useState("");
  const [date, setDate] = useState(() => urlDate || todayDateValue());
  const [timeFrom, setTimeFrom] = useState(() => urlFrom || "00:00");
  const [timeTo, setTimeTo] = useState(() => urlTo || currentTimeValue());
  const [segments, setSegments] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [playbackUrl, setPlaybackUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [opening, setOpening] = useState(false);
  const [error, setError] = useState("");
  const [searched, setSearched] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const selectedSegment = selectedIndex >= 0 ? segments[selectedIndex] : null;
  const timezoneLabel = useMemo(() => describeSingleTimezone(cameraTimezone), [cameraTimezone]);

  useEffect(() => {
    let cancelled = false;
    getCamera(cameraId)
      .then((camera) => {
        if (cancelled) return;
        if (camera?.name) setCameraName(camera.name);
        setCameraTimezone(camera?.display_timezone || "");
      })
      .catch(() => { /* имя и tz подхватятся из video settings / архива */ });
    return () => { cancelled = true; };
  }, [cameraId]);

  useEffect(() => {
    getVideoSettings(cameraId)
      .then((settings) => {
        setCameraName((prev) => settings?.camera?.name || prev);
        // archive_enabled=false означает лишь остановку дальнейшей записи, а не
        // запрет доступа к уже накопленному архиву — не блокируем просмотр.
        // Запись архива здесь не возобновляется: только чтение существующих сегментов.
      })
      .catch((settingsError) => {
        setError(getErrorMessage(settingsError));
        if (settingsError?.response?.status === 404) setNotFound(true);
      });
  }, [cameraId]);

  useEffect(() => {
    if (!notFound) return;
    const timer = setTimeout(() => {
      if (location.key === "default") {
        navigate(fallback, { replace: true });
      } else {
        navigate(-1);
      }
    }, 1800);
    return () => clearTimeout(timer);
  }, [notFound, navigate, fallback, location.key]);

  const handleShowRecords = async () => {
    const next = new URLSearchParams(searchParams);
    next.set("date", date || todayDateValue());
    next.set("from", timeFrom);
    next.set("to", timeTo);
    setSearchParams(next, { replace: true });

    setLoading(true);
    setError("");
    setPlaybackUrl("");
    setSelectedIndex(-1);
    setSearched(true);

    try {
      const data = await listArchiveRange(cameraId, {
        date: date || todayDateValue(),
        time_from: normalizeTimeParam(timeFrom, "00:00:00"),
        time_to: normalizeTimeParam(timeTo, "23:59:59", "59"),
      });
      setCameraName(data?.camera?.name || cameraName);
      setSegments(normalizeSegments(data));
    } catch (archiveError) {
      setSegments([]);
      setError(getErrorMessage(archiveError));
    } finally {
      setLoading(false);
    }
  };

  // Интервал пришёл в адресе (Back, reload, ссылка) — записи сразу загружаются.
  useEffect(() => {
    if (urlDate) handleShowRecords();
    // Только при открытии страницы камеры: дальше поиск запускает кнопка.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraId]);

  const handleOpenSegment = async (segment, index) => {
    setOpening(true);
    setError("");
    setSelectedIndex(index);
    setPlaybackUrl("");

    try {
      const data = await getArchiveSegmentUrl(cameraId, segment.segment_ref, 600);
      setPlaybackUrl(data?.url || data?.playback_url || data?.presigned_url || "");
    } catch (segmentError) {
      setError(getErrorMessage(segmentError));
    } finally {
      setOpening(false);
    }
  };

  const handlePrev = () => {
    if (selectedIndex <= 0) return;
    handleOpenSegment(segments[selectedIndex - 1], selectedIndex - 1);
  };

  const handleNext = () => {
    if (selectedIndex < 0 || selectedIndex >= segments.length - 1) return;
    handleOpenSegment(segments[selectedIndex + 1], selectedIndex + 1);
  };

  const handleClosePlayer = () => {
    setSelectedIndex(-1);
    setPlaybackUrl("");
  };

  return (
    <div className="archive-page">
      <div className="archive-header">
        <button className="archive-back-btn" onClick={() => navigate(-1)}>
          <ChevronLeft className="g-icon-lead" width={16} height={16} />Назад
        </button>
        <h1>Архив</h1>
      </div>

      <div className="archive-meta">
        <strong>{cameraName}</strong>
        <span>{timezoneLabel}</span>
      </div>

      <div className="archive-controls">
        <label>
          Дата
          <DateInput value={date} onChange={setDate} ariaLabel="Дата архива" />
        </label>
        <label>
          Время от
          <TimeInput value={timeFrom} onChange={setTimeFrom} ariaLabel="Время от" />
        </label>
        <label>
          Время до
          <TimeInput value={timeTo} onChange={setTimeTo} ariaLabel="Время до" />
        </label>
        <button className="archive-action-btn" onClick={handleShowRecords} disabled={loading}>
          Показать записи
        </button>
      </div>

      {loading && <div className="archive-status">Загрузка архива…</div>}
      {error && <div className="archive-error">{error}</div>}
      {searched && !loading && !error && segments.length === 0 && (
        <div className="archive-empty">За выбранный период записей нет</div>
      )}

      {segments.length > 0 && (
        <div className={`archive-list ${selectedSegment ? "archive-list--compact" : ""}`.trim()}>
          {segments.map((segment, index) => (
            <div key={segment.segment_ref} className={`archive-record ${index === selectedIndex ? "is-active" : ""}`}>
              <span>{formatSegmentTime(segment.started_at_local)}–{formatSegmentTime(segment.ended_at_local)}</span>
              <span>{formatSize(segment.size_bytes)}</span>
              <button className="archive-open-btn" onClick={() => handleOpenSegment(segment, index)} disabled={opening}>
                Открыть
              </button>
            </div>
          ))}
        </div>
      )}

      {selectedSegment && (
        <div className="archive-player">
          <div className="archive-player-toolbar">
            <div>
              <strong>{cameraName}</strong>
              <span>
                {formatSegmentTime(selectedSegment.started_at_local)}–{formatSegmentTime(selectedSegment.ended_at_local)}
              </span>
            </div>
            <button className="archive-action-btn" onClick={handlePrev} disabled={selectedIndex <= 0 || opening}>Предыдущая запись</button>
            <button className="archive-action-btn" onClick={handleNext} disabled={selectedIndex >= segments.length - 1 || opening}>Следующая запись</button>
            <button className="archive-action-btn" onClick={handleClosePlayer}>Закрыть плеер</button>
          </div>
          {opening && <div className="archive-status">Открываем запись…</div>}
          {playbackUrl && <video src={playbackUrl} controls playsInline />}
        </div>
      )}
    </div>
  );
};

export default ArchivePage;
